<template>
  <div class="report-trend-chart">
    <div v-if="points.length" class="report-trend-chart__canvas">
      <line-chart :data="chartData" :options="chartOptions" />
    </div>
    <div
      v-if="showDelta && deltaText"
      class="body3 text-indigo-medium text-right"
      data-testid="report-trend-chart-delta"
      v-html="deltaText"
    ></div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType } from "vue";
import LineChart from "@/components/LineChart.vue";
import { Chart as ChartJS, Filler } from "chart.js";
import type { ChartData, ChartOptions, ScriptableContext } from "chart.js";
import { useTheme } from "vuetify";
import { getFormattedDateTime } from "@/_helpers/date";
import { formatReportTrendCaption } from "@/_helpers/reports";
import { DoughnutChartColors } from "@/constants/charts";
import type { TrendPoint } from "@/_store/reports.module";

// Registered here rather than in the shared LineChart: this is the only chart that fills under
// its line, and Filler is inert for datasets that do not set `fill`.
ChartJS.register(Filler);

/**
 * Chart.js scriptable colour for the area under a sparkline: the line's blue fading downward.
 * @param context - The scriptable context, used for the canvas and plot bounds.
 * @returns A vertical gradient, or a transparent fill before the plot area is measured.
 */
const createAreaFill = (context: ScriptableContext<"line">): CanvasGradient | string => {
  const { ctx, chartArea } = context.chart;
  if (!chartArea) {
    return `${DoughnutChartColors.BLUE}00`;
  }
  const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
  // Faint and fading out well before the baseline: the design uses this as a wash under the line,
  // and because a flat series sits near the top of the plot area, a strong first stop reads as a
  // solid block rather than a gradient.
  gradient.addColorStop(0, `${DoughnutChartColors.BLUE}26`);
  gradient.addColorStop(0.7, `${DoughnutChartColors.BLUE}0A`);
  gradient.addColorStop(1, `${DoughnutChartColors.BLUE}00`);
  return gradient;
};

/**
 * Chart.js scriptable colour: a horizontal pink-to-blue gradient spanning the plot area.
 * Chart.js calls this on every render, including the first pass before layout has run, when
 * `chartArea` is still undefined — hence the flat fallback rather than an early `null`, which is not
 * a valid Chart.js `Color` and makes the whole dataset fail to type-check.
 * @param context - The scriptable context Chart.js passes in, used for the canvas and plot bounds.
 * @returns The gradient once the plot area is measured, otherwise {@link DoughnutChartColors.PINK}
 * as a flat stand-in for that first frame.
 */
const createGradient = (context: ScriptableContext<"line">): CanvasGradient | string => {
  const { ctx, chartArea } = context.chart;
  if (!chartArea) {
    return DoughnutChartColors.PINK;
  }
  const gradient = ctx.createLinearGradient(chartArea.left, 0, chartArea.right, 0);
  gradient.addColorStop(0, DoughnutChartColors.PINK);
  gradient.addColorStop(1, DoughnutChartColors.BLUE);
  return gradient;
};

export default defineComponent({
  components: { LineChart },
  props: {
    points: {
      type: Array as PropType<TrendPoint[]>,
      required: true,
    },
    delta: {
      type: Number,
      default: undefined,
    },
    showAxes: {
      type: Boolean,
      default: true,
    },
    /**
     * The KPI strip renders the caption itself, on the label's baseline in its own grid row,
     * which is where the design puts it — under the sparkline is the module-section placement.
     */
    showDelta: {
      type: Boolean,
      default: true,
    },
    /** The design fills the area under the KPI sparklines, but not under the module trends. */
    filled: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const theme = useTheme();

    // An all-zero series keeps its chart — a flat baseline says "measured, and it was zero",
    // which is not the same as having no data at all. What it must not keep is the y axis
    // Chart.js invents for it: with nothing above zero the scale auto-fits to 0-1 and prints a
    // "1" tick for a value the period never reached, which reads as a broken chart.
    const hasSeries = computed(() => props.points.some(({ value }) => value > 0));

    const sortedPoints = computed(() => [...props.points].sort((a, b) => a.date - b.date));

    const chartData = computed<ChartData<"line">>(() => ({
      labels: sortedPoints.value.map(({ date }) => getFormattedDateTime(date, "l")),
      datasets: [
        {
          data: sortedPoints.value.map(({ value }) => value),
          tension: 0.4,
          borderWidth: 1.5,
          pointRadius: 0,
          pointHoverRadius: 5,
          pointHoverBackgroundColor: "white",
          // Only borderColor: points are invisible until hover (pointRadius 0) and hover paints
          // them white, so a scriptable pointBackgroundColor rebuilt the gradient once per point
          // per frame to fill something never drawn.
          borderColor: createGradient,
          fill: props.filled ? "origin" : false,
          // Guarded, not unconditional: Chart.js resolves every `elements.line` key — backgroundColor
          // among them — before the Filler plugin ever looks at `fill`, so leaving the scriptable in
          // place built a three-stop gradient on all six module charts that never paint one.
          backgroundColor: props.filled ? createAreaFill : "transparent",
        },
      ],
    }));

    const chartOptions = computed<ChartOptions<"line">>(() => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      plugins: { legend: { display: false } },
      scales: {
        x: { display: false, grid: { display: false } },
        y: {
          display: props.showAxes,
          min: 0,
          grid: { color: theme.current.value.colors["indigo-faint"] },
          ticks: {
            display: hasSeries.value,
            precision: 0,
            color: theme.current.value.colors["indigo-light"],
          },
        },
      },
    }));

    const deltaText = computed(() => formatReportTrendCaption(props.delta));

    return { chartData, chartOptions, deltaText, hasSeries };
  },
});
</script>

<style scoped lang="scss">
.report-trend-chart {
  min-width: 0;

  // Bottom-aligned so the caption keeps its place on the chart's baseline even when there is no
  // chart: a module with no activity in the period renders the caption alone, and left to grow
  // from the top it floated level with the headline number instead of sitting under it.
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  height: 100%;
}

.report-trend-chart__canvas {
  height: 72px;

  // Chart.js writes an inline pixel width onto the canvas and re-measures its container
  // asynchronously, so a canvas can stay wider than its box for a frame or more after the
  // layout narrows. The report card clips (overflow: hidden), which would silently cut the
  // chart, so cap the canvas in CSS rather than racing the ResizeObserver.
  :deep(canvas) {
    max-width: 100%;
  }
}
</style>
